import React from 'react'
import { BreadcrumbView } from '../../../../../context/breadcrumb.context'
import { useApiListOrgSso, useApiFormOrgSso, useApiListOrgSsoConsentRequest } from '../../../../../api-new/state/auth'
import { useRoleOrgSSO } from '../../../../../role/hook/role-org'
import { useFeatureSSO } from '../../../../../shared/component/org-feature'
import { useLoad } from '../../../../../shared/hooks/load'
import { IconShield } from '../../../../../shared/icon/icon'
import { useQueryParam } from '../../../../../shared/hooks/location'
import { WorkspaceSection } from '../../../../../shared/sso-workspace/workspace-shell'
import { connectionUse, ConnectionUsePill } from '../../../../../shared/sso-workspace/connection-use'
import { ProviderLogo } from '../../../../../shared/sso-workspace/provider-logo'
import { ConnectProvider } from '../../../../../shared/sso-consent/connect-provider'
import { ConsentRequestList } from '../../../../../shared/sso-consent/consent-request-list'
import '../../../../../shared/sso-consent/sso-consent.scss'
import { OrgSsoDomain } from './org-sso-domain'
import { OrgSsoRecipient } from './org-sso-recipient'
import { useAuthContext } from '../../../../../context/auth.context'
import { useAuditRecord } from '../../../../../shared/audit/history/audit-record-context'

// What kind of connection a row is, in words (never the raw driver /
// connection fields).
export const CONNECTION_KIND = {
  shared_app: "EducationXR's shared app",
  own_registration: "Your organization's own app registration"
}

const connectionName = connection => connection.displayName || connection.providerName

// The organization's Authentication page, in the connection workspace's
// language (spec 2026-09-26 design pass, review I5). Plan-gated on
// featureSSO: without it the organization sees one notice, since every
// org-side SSO endpoint refuses anyway. With it:
//
//   - Connections: every connection linked to the organization, owned or
//     not (audit B6), with its kind, the domains it signs in and its status.
//     One EducationXR manages is labelled so, with who to ask.
//   - Domains: claim, verify (TXT record), require SSO, bind and unbind
//     (org-sso-domain.js).
//   - Alert recipients (org-sso-recipient.js).
//   - Connect a sign-in provider and Pending approvals (the same
//     ConnectProvider and ConsentRequestList the staff pages use, scoped to
//     the caller's own organization -- the backend takes it from the JWT).
//
// Every action follows ORG_SSO, the permission the server checks (B8).
export const OrgSso = () => {
  const featureSSO = useFeatureSSO()
  const authContext = useAuthContext()

  // History here is the organization's, with its domains, connections and
  // alert recipients (its child records); the page shows no one connection
  // of its own. No editor role: content opens an organization's History
  // only to audit-log readers (core event.RecordScope has no entry for
  // organizations), so ORG_SSO alone would only be refused.
  useAuditRecord({ type: 'organizations', id: authContext.state.organizationID, children: true })

  if (!featureSSO) {
    return (
      <BreadcrumbView Icon={IconShield} text="Authentication">
        <div className="sw-workspace sw-pane">
          <WorkspaceSection id="plan" title="Single sign-on">
            <p className="mb-0">Single sign-on is not included in your organization's plan. Contact EducationXR to enable it.</p>
          </WorkspaceSection>
        </div>
      </BreadcrumbView>
    )
  }

  return <OrgSsoContent />
}

const OrgSsoContent = () => {
  const apiListOrgSso = useApiListOrgSso()
  const orgSso = useApiFormOrgSso()

  // ORG_SSO, the permission every /org-sso endpoint checks (audit B8).
  const roleOrgSSO = useRoleOrgSSO()

  const apiListConsentRequest = useApiListOrgSsoConsentRequest()
  const initialTenant = useQueryParam('tenant') || ''

  useLoad(() => Promise.all([apiListOrgSso.get(), apiListConsentRequest.get()]), [])

  const handleRefresh = async () => await apiListOrgSso.get()
  const handleRefreshRequests = async () => await apiListConsentRequest.get()

  const handleRequest = ({ tenant, sendTo }) => orgSso.postArg('/entra/request', { tenant, sendTo })
  const handleResend = async id => { const result = await orgSso.postArg(`/consent-requests/${id}/resend`, {}); await handleRefreshRequests(); return result }
  const handleCancel = async id => { const result = await orgSso.postArg(`/consent-requests/${id}/cancel`); await handleRefreshRequests(); return result }

  const handleGoogle = async ({ domain, name }) => {
    await orgSso.postArg('/google', { domain, providerName: name, displayName: name })
    await handleRefresh()
  }

  const connections = apiListOrgSso.items

  return (
    <BreadcrumbView Icon={IconShield} text="Authentication">
      {/* The page is taller than the pane it sits in: it scrolls itself
          (the layout around it is overflow-hidden). */}
      <div className="sw-workspace sw-pane">
        <header className="mb-3">
          <h1 className="sw-title">Single sign-on</h1>
          <div className="sw-sub">Let people at your organization sign in to EducationXR with the work accounts they already use.</div>
        </header>

        <WorkspaceSection id="connections" title="Connections" lede="The identity providers people here sign in through.">
          {connections.length === 0
            ? <div className="sc-empty-state">
              <p>No connections yet. Connect Microsoft or Google below so people here can sign in with their work accounts.</p>
            </div>
            : <ul className="sc-rows">
              {connections.map(connection => {
                // Switched on, not public, and no domain of ANY organization
                // bound (boundDomainCount): nobody is sent to it.
                const use = connectionUse({ active: connection.active, isPublic: connection.public, bound: connection.boundDomainCount })
                return (
                  <li key={connection.id} className="sc-row" data-testid={`connection-${connection.id}`}>
                    <ProviderLogo iconSVG={connection.iconSVG} />
                    <div className="sc-row-main">
                      <span className="fw-semibold">{connectionName(connection)}</span>
                      <span className="sw-hint">
                        {[CONNECTION_KIND[connection.connection], connection.owned ? 'Managed by your organization' : 'Managed by EducationXR'].filter(Boolean).join(' · ')}
                      </span>
                      {/* Not in use says why, which also covers "none of yours". */}
                      <span className="sw-hint">
                        {use.reason ||
                          ((connection.domains || []).length > 0
                            ? `Signs in people at ${connection.domains.map(d => d.domain).join(', ')}.`
                            : 'No domains of yours are bound to it yet.')}
                      </span>
                      {/* The subtitle already says EducationXR manages it: once is enough. */}
                      {!connection.owned && <span className="sw-hint">Contact support to change it.</span>}
                    </div>
                    <ConnectionUsePill use={use} />
                  </li>
                )
              })}
            </ul>}
        </WorkspaceSection>

        <WorkspaceSection id="domains" title="Domains"
          lede="The email domains your organization has claimed. Verify a domain, then bind it to a connection to send its people to single sign-on.">
          <OrgSsoDomain connections={connections} enabled={roleOrgSSO.accessUpdate} onConnectionsChanged={handleRefresh} />
        </WorkspaceSection>

        <WorkspaceSection id="recipients" title="Alert recipients"
          lede="Who hears about your single sign-on alerts: domains at risk or deactivated, and expiring credentials on connections your organization owns.">
          <OrgSsoRecipient enabled={roleOrgSSO.accessUpdate} />
        </WorkspaceSection>

        <WorkspaceSection id="connect" title="Connect a sign-in provider"
          lede="Let people at your organization sign in to EducationXR with the accounts they already use.">
          <ConnectProvider
            audience="org"
            enabled={roleOrgSSO.accessUpdate}
            initialTenant={initialTenant}
            onRequest={handleRequest}
            onRequested={handleRefreshRequests}
            onResend={handleResend}
            onCancel={handleCancel}
            onGoogle={handleGoogle}
          />
        </WorkspaceSection>

        <WorkspaceSection id="approvals" title="Pending approvals"
          lede="Requests sent to your IT administrator. Each link works for 7 days; sending a new one for the same Microsoft organization replaces the old link.">
          <ConsentRequestList
            rows={apiListConsentRequest.items}
            loaded={apiListConsentRequest.loaded !== false}
            enabled={roleOrgSSO.accessUpdate}
            initialFilter="pending"
            emptyText="No approval requests yet. Send one above and it shows here until your IT administrator approves it."
            onLink={id => orgSso.get(`consent-requests/${id}/link`)}
            onResend={id => orgSso.postArg(`/consent-requests/${id}/resend`, {})}
            onCancel={id => orgSso.postArg(`/consent-requests/${id}/cancel`)}
            onChanged={handleRefreshRequests}
          />
        </WorkspaceSection>
      </div>
    </BreadcrumbView>
  )
}
